import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { locationCardStyles } from './styles';
import { Edit, Delete } from '@mui/icons-material';
import { NotesIcon } from 'components/Shared/Icons/tooltip_icons';
import { Location } from 'types/entities/types';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditLocation from './AddOrEditLocation';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: Location;
  showActionButtons: boolean;
  onLocationDelete?: () => void;
  onLocationUpdate?: () => void;
}

const LocationCard = (props: Props) => {
  const { option: location, showActionButtons = true, onLocationDelete, onLocationUpdate } = props;

  const [editLocationModalOpen, setEditLocationModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<ErrorResponse | null>();

  const deleteLocation = () => {
    axiosService
      .delete(`/entities/location/${location.id}/`)
      .then(() => {
        if (onLocationDelete) {
          onLocationDelete();
        }
      })
      .catch(error => setDeleteError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onLocationEdit = () => {
    if (onLocationUpdate) {
      onLocationUpdate();
    }
    setEditLocationModalOpen(false);
  };

  return (
    <>
      <Card elevation={1} sx={locationCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={locationCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditLocationModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={locationCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={location.name} tooltipText={location.id} />}
          subheader="Location"
          sx={locationCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={locationCardStyles.contactBox}>
            <NotesIcon />
            <Typography color="text" sx={locationCardStyles.contactValue}>
              {location.notes || '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete location</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete location <b>{location.name}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteLocation} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditLocation
        location={location}
        onSave={onLocationEdit}
        modalTitle="Edit location"
        isModalOpen={editLocationModalOpen}
        setModalOpen={setEditLocationModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup
        title={deleteError?.statusText}
        message={deleteError?.message}
        onClose={() => setDeleteError(null)}
      />
    </>
  );
};

export default LocationCard;
